import type { BoardCalendar } from "./boardCalendar";
import type {
  InteractionRequest,
  ResultRef,
  FieldRef,
  InteractionDiagnostic,
  InteractionEntityType,
  InteractionSubject,
  InteractionTimeUnit,
  InteractionValue,
  InteractionValueType,
  PanelInteraction,
  ResultContext,
  ResultRevision,
  SerializedInteractionValue,
  TimelineProvenance,
} from "./interactionTypes";

export const MARK_TYPES = [
  "bar",
  "line",
  "area",
  "point",
  "arc",
  "rect",
  "boxplot",
  "text",
] as const;
export type MarkType = (typeof MARK_TYPES)[number];
export const MARK_LINEAGE_AGGREGATES = [
  "count",
  "sum",
  "mean",
  "median",
  "min",
  "max",
  "valid",
] as const;
export type MarkLineageAggregate = (typeof MARK_LINEAGE_AGGREGATES)[number];
export const MARK_LINEAGE_CALENDAR_UNITS = [
  "yearmonthdate",
  "yearmonth",
  "hours",
  "day",
  "yearweek",
] as const;
export type MarkLineageCalendarUnit =
  (typeof MARK_LINEAGE_CALENDAR_UNITS)[number];
export const MARK_LINEAGE_GRAINS = [
  "row",
  "group",
  "bin",
  "calendar",
  "cell",
  "statistic",
  "scalar",
] as const;
export type MarkLineageGrain = (typeof MARK_LINEAGE_GRAINS)[number];
export const MARK_LINEAGE_ENCODINGS = [
  "x",
  "y",
  "color",
  "size",
  "row",
  "column",
  "theta",
  "text",
  "xOffset",
] as const;
export type MarkLineageEncoding = (typeof MARK_LINEAGE_ENCODINGS)[number];

/** A transform output field, named with the mark lineage that produces it; it is never a column of the bound stage. */
export interface LineageFieldRef {
  lineage: string;
  field: string;
}
export type UnitViewPath = (string | number)[];
/** A calendar grouping as a mark lineage or a group dimension mapping
 *  declares it: the unit it groups by and the epoch of the instants it
 *  reads. The time zone and week start come from the board's calendar. */
export interface CalendarGrouping {
  unit: MarkLineageCalendarUnit;
  epoch: string;
}
/** A calendar grouping as a published group carries it: with the time zone
 *  and week start of the board calendar the group was cut in, which the
 *  group's selection SQL and its text read. */
export type ZonedCalendarGrouping = CalendarGrouping & BoardCalendar;
/**
 * The exact origin and unit of numeric bins: a source value minus the origin, divided by the unit, is the number Vega
 * draws.
 */
export interface BinGrid {
  origin: SerializedInteractionValue;
  unit: SerializedInteractionValue;
}
export type BinParameters =
  | { kind: "automatic" }
  | { kind: "maxbins"; maxbins: number }
  | { kind: "step"; step: number; extent?: [number, number]; anchor?: number }
  | { kind: "edges"; edges: SerializedInteractionValue[] };

export type MarkLineageDimension = {
  id: string;
  source: FieldRef;
  valueType: InteractionValueType;
  output: LineageFieldRef;
  encoding?: MarkLineageEncoding;
} & (
  | { kind: "identity" }
  | {
      kind: "bin";
      parameters: BinParameters;
      upper: LineageFieldRef;
      binGrid?: BinGrid;
    }
  | { kind: "calendar"; calendar: CalendarGrouping }
);
export interface MarkLineageMeasure {
  id: string;
  source?: FieldRef;
  sourceType?: ExactValueType;
  encoding?: MarkLineageEncoding;
  output: LineageFieldRef;
  valueType: ExactValueType;
  aggregate?: MarkLineageAggregate;
  role: "measure" | "reference";
  additive: boolean;
}
export type MarkLineageTransform =
  | { kind: "aggregate"; dimensions: string[]; measures: string[] }
  | { kind: "bin"; dimension: string }
  | { kind: "calendar"; dimension: string }
  | {
      kind: "stack";
      measure: string;
      groupBy: string[];
      series: string[];
      offset: "zero" | "normalize" | "center";
      start: LineageFieldRef;
      end: LineageFieldRef;
    }
  | {
      kind: "fold";
      fields: FieldRef[];
      key: LineageFieldRef;
      value: LineageFieldRef;
    }
  | {
      kind: "boxplot";
      source: FieldRef;
      groupBy: string[];
      extent: { kind: "iqr"; multiplier: number } | { kind: "min-max" };
      outputs: {
        lower: LineageFieldRef;
        q1: LineageFieldRef;
        median: LineageFieldRef;
        q3: LineageFieldRef;
        upper: LineageFieldRef;
      };
    };
export const MARK_ROLES = [
  "mark",
  "box",
  "median",
  "whisker",
  "cap",
  "outlier",
  "reference",
] as const;
export type MarkRole = (typeof MARK_ROLES)[number];
export type TemporalDuration = { ticks: string; unit: InteractionTimeUnit };
export type TemporalMatchPolicy =
  { kind: "exact" } | { kind: "nearest"; tolerance: TemporalDuration };
export interface MarkLineageCoordinate {
  source: FieldRef;
  output: LineageFieldRef;
  valueType: InteractionValueType;
  matching: TemporalMatchPolicy;
  encoding?: MarkLineageEncoding;
  comparableSources?: { epoch: string; fields: FieldRef[] };
}
export interface MarkLineage {
  id: string;
  bindingName: string;
  unitViewPath: UnitViewPath;
  markType: MarkType;
  grain: MarkLineageGrain;
  markRoles: MarkRole[];
  dimensions: MarkLineageDimension[];
  /** Dimension ids whose values split the marks into series; a tooltip entry's series label joins them in this order. */
  series: string[];
  measures: MarkLineageMeasure[];
  transforms: MarkLineageTransform[];
  coordinate?: MarkLineageCoordinate;
  total?: { measures: string[]; label?: string };
}

/** The calendar group's components: exactly those its unit names, so `hours` and `day` carry only that component. */
export interface CalendarComponents {
  /** For `yearweek`, the year, month and date of the week's first day, which falls on the grouping's week start. */
  year?: number;
  month?: number;
  date?: number;
  hours?: number;
  day?: number;
}
export type GroupDimensionValue = {
  id: string;
  source: FieldRef;
  valueType: InteractionValueType;
} & (
  | {
      kind: "null";
      transform: "identity" | "bin" | "calendar";
      calendar?: CalendarGrouping;
      binGrid?: BinGrid;
    }
  | { kind: "identity"; value: SerializedInteractionValue }
  /**
   * The fold transform's key names one of these source fields; the key is a transform output field, not a column of
   * the bound stage.
   */
  | { kind: "fold"; fields: FieldRef[] }
  | {
      kind: "bin";
      lower: SerializedInteractionValue;
      upper: SerializedInteractionValue;
      lowerInclusive: boolean;
      upperInclusive: boolean;
      binGrid?: BinGrid;
    }
  | {
      kind: "calendar";
      calendar: ZonedCalendarGrouping;
      components: CalendarComponents;
    }
);
export interface RenderedGroup {
  markLineageId: string;
  sourceRevision: ResultRevision;
  outputRevision: ResultRevision;
  outputRowKey: string;
  resultContext: ResultContext;
  dimensions: GroupDimensionValue[];
}
/**
 * Maps one rendered-group dimension onto the table a selection binding or detail target reads. Every dimension needs
 * exactly one mapping: to a source field (with the same bin grid or calendar grouping) or to transform output fields.
 */
export type GroupDimensionMapping = {
  dimension: string;
  valueType: InteractionValueType;
} & (
  | { kind: "identity"; field: string }
  | { kind: "bin-source"; field: string; binGrid?: BinGrid }
  | {
      kind: "bin-output";
      field: string;
      upperField: string;
      binGrid?: BinGrid;
    }
  | { kind: "calendar-source"; field: string; calendar: CalendarGrouping }
  | { kind: "calendar-output"; field: string; calendar: CalendarGrouping }
  | { kind: "fold-output"; field: string; fields: FieldRef[] }
  | { kind: "fold-source"; sources: { source: FieldRef; field: string }[] }
);

export interface CompiledMarkLineages {
  markLineages: MarkLineage[];
  requiredOutputFields: LineageFieldRef[];
  diagnostics: InteractionDiagnostic[];
  capabilities: {
    markLineageId: string;
    tooltip: boolean;
    interactions: boolean;
  }[];
}

/**
 * An Arrow decimal kept exactly, as its unscaled coefficient and scale. A chart draws it at the nearest number, and
 * its mark carries that number. A result's Decimal column of scale zero reads as integers, not as these.
 */
export interface ExactDecimalValue {
  type: "decimal";
  coefficient: bigint;
  scale: number;
  precision: number;
  /** The Arrow decimal width that holds it; a value read from a 64- or 32-bit Arrow decimal is held at 128. */
  bitWidth: 128 | 256;
}
export type ExactValueType =
  | InteractionValueType
  | {
      type: "decimal";
      scale: number;
      precision: number;
      bitWidth: 128 | 256;
      nullable?: boolean;
    };
export type ExactValue = InteractionValue | ExactDecimalValue;
export type SerializedExactValue =
  | SerializedInteractionValue
  | (Omit<ExactDecimalValue, "coefficient"> & { coefficient: string });
export type ExactValueLookup =
  | { status: "available"; value: ExactValue }
  | { status: "unavailable"; message: string };

/**
 * The topic a timestamp was read from, as a person knows it: the topic's name, and the timeline source its rows
 * are timestamped against, by kind (`message_log_time`, `message_publish_time` or `schema_field`) and, for a schema
 * field, the field's path with its parts joined by dots.
 */
export interface TimestampTopic {
  name: string;
  timelineSourceKind: string;
  timelineSourceField?: string;
}
export interface TemporalFieldProvenance {
  recorded: { ticks: bigint; unit: InteractionTimeUnit };
  normalized: Extract<InteractionValue, { type: "instant" }>;
  readFrom: TimelineProvenance;
  topic: TimestampTopic;
}
export type TemporalProvenanceLookup =
  | { status: "available"; sources: TemporalFieldProvenance[] }
  | { status: "unavailable"; message: string };
export interface MarkLineageSourceRow {
  rowKey: string;
  fields: Record<string, ExactValue>;
  /**
   * Provenance is per field, since a combine stage (join, as-of join or union) can read timestamps from more than one
   * topic.
   */
  provenance: Record<string, TemporalProvenanceLookup>;
}
export interface RenderedMarkLineageRows {
  /** A number the chart assigns each time it prepares a lineage's drawn rows, naming that preparation. */
  generation: string;
  /** Each `rowKey` is the renderer's key for a drawn row, which `subject` and `contributingRowKeys` take once prepared. */
  rows: {
    rowKey: string;
    values: Record<string, SerializedInteractionValue>;
    dimensions: GroupDimensionValue[];
    role: MarkRole;
    /**
     * `imputed` marks a row Vega's `impute` transform added (it lacks the field the renderer sets to `true` on every
     * source row before the transform runs): it has no source rows, interactions, matches or totals.
     */
    rowOrigin?: "observed" | "imputed";
    sourceRowKeys?: string[];
  }[];
}
export interface PrepareMarkLineageRowsInput {
  source: ResultRef;
  lineage: MarkLineage;
  rendered: RenderedMarkLineageRows;
  signal: AbortSignal;
}
export type PreparedMarkLineageRows =
  | {
      status: "ready";
      markLineageId: string;
      sourceRevision: ResultRevision;
      revision: ResultRevision;
      renderedGeneration: string;
      resultContext: ResultContext;
      output: ResultRef;
      coverage: ResultContext["coverage"];
      /** The renderer's row key of every drawn row, in the order the rows were rendered. */
      rendererRowKeys: readonly string[];
      /**
       * The prepared output row of every drawn row, by the renderer's row key; `subject` returns nothing for an
       * `imputed` row.
       */
      outputRow(
        rendererRowKey: string,
      ):
        | Readonly<{ rowKey: string; rowOrigin: "observed" | "imputed" }>
        | undefined;
      /** The renderer's row key of the drawn row whose prepared output row is `outputRowKey`. */
      rendererRowKey(outputRowKey: string): string | undefined;
      subject(rendererRowKey: string): InteractionSubject | undefined;
      contributingRowKeys(
        rendererRowKey: string,
      ): readonly string[] | undefined;
      sourceRow(sourceRowKey: string): MarkLineageSourceRow | undefined;
      release(): Promise<void>;
    }
  | {
      status: "unavailable";
      diagnostics: InteractionDiagnostic[];
    };

export interface CoordinateSources {
  epoch: string;
  sources: (
    | {
        kind: "topic";
        field: FieldRef;
        topicId: string;
        partitionId: string;
        timelineSource: string;
        offsetNs: bigint;
      }
    | {
        kind: "derived";
        field: FieldRef;
        revision: ResultRevision;
        epoch: string;
      }
  )[];
}
/** One drawn row of a series as temporal matching reads it: its renderer row key and its instant, with the source
 *  field the instant was read from and the instant's provenance. */
export interface TemporalRow {
  rowKey: string;
  coordinateField: FieldRef;
  coordinate: Extract<InteractionValue, { type: "instant" }>;
  provenance: TemporalProvenanceLookup;
}
export interface TemporalSeriesInput {
  revision: ResultRevision;
  coordinateSources: CoordinateSources;
  series: {
    key: string;
    label: string;
    rows: TemporalRow[];
  }[];
}
export interface TemporalMatchRequest {
  coordinate: Extract<InteractionValue, { type: "instant" }>;
  /**
   * A declared policy, or `nearest-within-series`: each series' row
   * nearest the coordinate, however far, when the coordinate lies between
   * the series' first and last rows. A chart whose downsample leaves rows
   * out matches its series this way, since a series rarely has a drawn row at
   * the exact instant another one does.
   */
  policy: TemporalMatchPolicy | { kind: "nearest-within-series" };
}
export type TemporalMatchEntry = { seriesKey: string; label: string } & (
  | {
      status: "matched";
      rows: TemporalRow[];
      coordinate: Extract<InteractionValue, { type: "instant" }>;
      displacement: { ticks: bigint; unit: InteractionTimeUnit };
    }
  | { status: "missing" }
);
export interface TemporalMatchResult {
  /** One entry per series of the matched input, in the input's order. */
  entries: TemporalMatchEntry[];
}
declare const markLineageRegistrationBrand: unique symbol;
declare const entityRegistrationBrand: unique symbol;
export type MarkLineageRegistrationToken = string & {
  readonly [markLineageRegistrationBrand]: true;
};
export type EntityRegistrationToken = string & {
  readonly [entityRegistrationBrand]: true;
};
export interface MarkLineageInteractionPrecondition {
  kind: "mark-lineage";
  markLineageId: string;
  sourceRevision: ResultRevision;
  outputRevision: ResultRevision;
  renderedGeneration: string;
  resultContext: ResultContext;
  registration: MarkLineageRegistrationToken;
}
export interface EntitySourceRevision {
  source: string;
  generation: number;
  path: string;
}
/**
 * The result context of an entity source: it has no Arrow result and no stages it was computed from, and names the
 * entity source's revision.
 */
export interface EntityResultContext extends Omit<
  ResultContext,
  "result" | "computedFrom"
> {
  source: EntitySourceRevision;
}

export interface EntityInteractionPrecondition {
  kind: "entity";
  entityType: InteractionEntityType;
  entityId: string;
  bindingName: string;
  sourceRevision: EntitySourceRevision;
  resultContext: EntityResultContext;
  registration: EntityRegistrationToken;
}
export interface ChosenMark {
  precondition: MarkLineageInteractionPrecondition;
  lineage: MarkLineage;
  subject: InteractionSubject;
  output: ResultRef;
  prepared: Extract<PreparedMarkLineageRows, { status: "ready" }>;
  /** The keys of the rows of the lineage's source result behind the chosen
   *  mark (`PreparedMarkLineageRows.contributingRowKeys`). Absent when a
   *  brush's interval chose the mark, and on a mark rebuilt from a request
   *  that ran. */
  contributingRowKeys?: readonly string[];
  requestedInteractionId?: string;
}
export interface TooltipEntry {
  key: string;
  markLineageId: string;
  unitViewPath: UnitViewPath;
  seriesKey: string;
  rowKey?: string;
  group?: RenderedGroup;
  revision: ResultRevision;
  role: MarkRole;
  label: string;
  status: "matched" | "missing";
  /**
   * The entry's fields. `sourceDetail` marks the timestamp a topic recorded
   * for the entry's coordinate, which the chart does not draw. `coordinate`
   * marks one read from the source field of the mark lineage's coordinate,
   * `inSeriesName` one whose text is part of `label`, and `measure` one read
   * from a measure whose role is `measure`, or a box plot statistic. `format`
   * is the d3 number format the chart writes the value in.
   */
  values: {
    label: string;
    value: ExactValue;
    sourceDetail?: true;
    coordinate?: true;
    inSeriesName?: true;
    measure?: true;
    format?: string;
  }[];
  /** Where the entry's mark spans two of its values, the labels of the
   *  values it spans from and to, and the title of what it spans. */
  span?: { label: string; start: string; end: string };
  coordinate?: Extract<InteractionValue, { type: "instant" }>;
  displacement?: { ticks: bigint; unit: InteractionTimeUnit };
  interactions: MarkInteraction[];
}
/** An interaction a mark runs, on the panel `panelId`: the request that runs
 *  it on what the mark gives it to act on, or, when the mark gives it nothing,
 *  why, in words for people. */
export type MarkInteraction =
  | {
      panelId: string;
      interaction: PanelInteraction;
      request: InteractionRequest;
    }
  | {
      panelId: string;
      interaction: PanelInteraction;
      request?: undefined;
      noSubjectReason: string;
    };
/** A drawn mark a tooltip circles: the mark's key in the chart's mark snapshot, and the key of the entry that lists
 *  its row. */
export interface TooltipMark {
  markKey: string;
  entryKey: string;
}
export interface TooltipContent {
  /** The value pointed at: the active row's coordinate, a Decimal coordinate as its exact Decimal rather than the
   *  number it is drawn at, or, where the row has no coordinate, the text of its listed values. */
  pointedValue: ExactValue;
  /** The title of the axis `pointedValue` is read on; absent where the active row has no coordinate. */
  pointedTitle?: string;
  entries: TooltipEntry[];
  activeKey: string;
  /** Each total under its title, with the d3 number format the chart writes
   *  its measure in. */
  totals: { label: string; value: ExactValue; format?: string }[];
  /** One sentence for each total the tooltip cannot show, saying why. */
  notices: string[];
  /** The drawn marks of the matched entries. */
  marks: TooltipMark[];
  /** How many series have only `missing` entries, apart from the active entry's series. Layers that draw the same
   *  series, such as a line and points of one measure, count it once. */
  otherSeriesWithoutValueCount: number;
}
